import { useCallback, useMemo } from 'react';

import type { AccountDetailsState } from '../signupUtils';
import type { AuthConnection } from './useCreateAccount';

const deriveUserAuthConnection = (sub?: string): AuthConnection =>
  sub?.includes('google-oauth2')
    ? 'googleAuthConnection'
    : 'passwordAuthConnection';

type UseAuthConnectionsOptions = {
  user?: { sub?: string };
  inviteId?: string;
  authConnections: Record<AuthConnection, boolean>;
  authConnectionsCustomized?: boolean;
  setAccountDetails: (
    updater: (current: AccountDetailsState) => AccountDetailsState,
  ) => void;
};

export type ToggleAuthConnectionOptions = {
  connection: AuthConnection;
  checked: boolean;
};

const resolveAuthConnections = ({
  user,
  inviteId,
  authConnections,
  authConnectionsCustomized,
  userAuthConnection,
}: Pick<
  UseAuthConnectionsOptions,
  'user' | 'inviteId' | 'authConnections' | 'authConnectionsCustomized'
> & {
  userAuthConnection: AuthConnection;
}): Record<AuthConnection, boolean> => {
  if (!user || authConnectionsCustomized) return authConnections;
  if (!inviteId) {
    return { googleAuthConnection: true, passwordAuthConnection: true };
  }
  return { ...authConnections, [userAuthConnection]: true };
};

export const useAuthConnections = ({
  user,
  inviteId,
  authConnections,
  authConnectionsCustomized,
  setAccountDetails,
}: UseAuthConnectionsOptions) => {
  const userAuthConnection = deriveUserAuthConnection(user?.sub);
  const resolvedAuthConnections = useMemo(
    () =>
      resolveAuthConnections({
        user,
        inviteId,
        authConnections,
        authConnectionsCustomized,
        userAuthConnection,
      }),
    [
      authConnections,
      authConnectionsCustomized,
      inviteId,
      user,
      userAuthConnection,
    ],
  );

  const toggleAuthConnection = useCallback(
    ({ connection, checked }: ToggleAuthConnectionOptions) => {
      setAccountDetails(current => ({
        ...current,
        authConnections: {
          ...resolveAuthConnections({
            user,
            inviteId,
            authConnections: current.authConnections,
            authConnectionsCustomized:
              current.authConnectionsCustomized || authConnectionsCustomized,
            userAuthConnection,
          }),
          [connection]: checked,
        },
        authConnectionsCustomized: true,
      }));
    },
    [
      authConnectionsCustomized,
      inviteId,
      setAccountDetails,
      user,
      userAuthConnection,
    ],
  );

  const hasSelectedAuthConnection = Object.values(resolvedAuthConnections).some(
    Boolean,
  );

  const showAuthConnections = !!inviteId;

  return {
    toggleAuthConnection,
    hasSelectedAuthConnection,
    showAuthConnections,
    userAuthConnection,
    authConnections: resolvedAuthConnections,
  };
};
